import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { TeamsIndexData } from '../../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTeamsStore } from '../../model/teams/store'

interface TeamsApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/teams` — the index. Returns `{ teams, users, meta }`; the `users` block is the
 * list of active users used by the create/edit member picker.
 */
export const useTeamsApi = async (options?: TeamsApiOptions): Promise<TeamsIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useTeamsStore()

  const url = useAdminRoutes('admin.teams.data', pick({
    q: store.getFilterSearch,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: TeamsIndexData | null = data.data || null

    if (indexData) {
      store.setTeams(indexData.teams)
      store.setMeta(indexData.meta)
      store.setUsers(indexData.users)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
